/**
 * Estado do editor de landing page — tudo que não é desenho de tela.
 *
 * O editor tinha 750 linhas porque estado e layout moravam juntos. Aqui fica o
 * que faz a página funcionar: adoção de versão, histórico de desfazer, as
 * operações sobre blocos, validação contínua, salvar e publicar, atalhos de
 * teclado. O componente que consome isto só compõe painéis.
 *
 * Decisões herdadas do arquivo antigo, com o porquê:
 *
 *  - **O que veio do servidor é adotado uma vez por versão** (`versaoAdotada`).
 *    Reagir a `sujo` voltando a `false` depois de salvar reimplantava a versão
 *    velha por cima do que acabara de ser salvo.
 *  - **Digitação contínua vira um passo só no histórico** (janela de 600ms) —
 *    um Ctrl+Z por caractere não é desfazer, é reescrever de trás para frente.
 *  - **A validação roda a cada mudança com as MESMAS funções do servidor** —
 *    o operador descobre agora, não no clique de publicar.
 *  - **O atalho de teclado é reinstalado a cada render, sem lista de
 *    dependências** — uma lista aqui é uma closure velha salvando a página
 *    errada.
 */

import {
  blocoPadrao,
  normalizarFormulario,
  novoIdBloco,
  validarFormulario,
  validarPagina,
  type Bloco,
  type PaginaLanding,
  type TipoBloco,
} from '@egba-do/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'

import { api, type Item } from '../../api/cliente'
import { paraRender as recorteRender, type LandingPagina } from './tipos'

export interface Problema {
  blocoId: string | null
  mensagem: string
}

export function useEditorPagina(idOuSlug: string) {
  const qc = useQueryClient()

  const [pagina, setPagina] = useState<LandingPagina | null>(null)
  const [selecionado, setSelecionado] = useState<string | null>(null)
  const [sujo, setSujo] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)

  const historico = useRef<LandingPagina[]>([])
  const ultimoPasso = useRef(0)
  const versaoAdotada = useRef<string | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['landing', idOuSlug],
    queryFn: () => api.get<Item<LandingPagina>>(`/api/v1/landing/${idOuSlug}`),
    retry: 1,
  })

  useEffect(() => {
    if (!data || versaoAdotada.current === data.dados.atualizadoEm) return
    versaoAdotada.current = data.dados.atualizadoEm
    historico.current = []
    setPagina(data.dados)
    setSujo(false)
  }, [data])

  const paraRender: PaginaLanding | null = useMemo(
    () => (pagina ? recorteRender(pagina) : null),
    [pagina],
  )

  const problemas: Problema[] = useMemo(() => {
    if (!paraRender) return []
    const lista: Problema[] = validarPagina(paraRender).map((p) => ({
      blocoId: p.bloco ?? null,
      mensagem: p.mensagem,
    }))
    for (const form of paraRender.blocos.filter((b) => b.tipo === 'formulario' && !b.oculto)) {
      const { campos, acoes } = normalizarFormulario(form.props as Record<string, unknown>)
      for (const p of validarFormulario(campos, acoes)) {
        lista.push({ blocoId: form.id, mensagem: p.mensagem })
      }
    }
    return lista
  }, [paraRender])

  const problemasPorBloco = useMemo(() => {
    const m = new Map<string, string[]>()
    for (const p of problemas) {
      if (!p.blocoId) continue
      m.set(p.blocoId, [...(m.get(p.blocoId) ?? []), p.mensagem])
    }
    return m
  }, [problemas])

  // ------------------------------------------------------------------ ações

  function alterar(mudanca: Partial<LandingPagina>): void {
    if (!pagina) return
    const agora = Date.now()
    if (agora - ultimoPasso.current > 600) {
      historico.current.push(pagina)
      if (historico.current.length > 60) historico.current.shift()
    }
    ultimoPasso.current = agora
    setPagina({ ...pagina, ...mudanca })
    setSujo(true)
  }

  function desfazer(): void {
    const anterior = historico.current.pop()
    if (!anterior) return
    ultimoPasso.current = 0
    setPagina(anterior)
    setSujo(true)
    setAviso(null)
  }

  function inserirBloco(tipo: TipoBloco, posicao?: number): void {
    if (!pagina) return
    const novo = blocoPadrao(tipo)
    const blocos = [...pagina.blocos]
    const iSelecionado = selecionado ? blocos.findIndex((b) => b.id === selecionado) : -1
    const iRodape = blocos.findIndex((b) => b.tipo === 'rodape')

    // Onde a pessoa está olhando: logo depois do bloco selecionado. Sem
    // seleção, acima do rodapé — bloco solto depois do rodapé é bloco que
    // ninguém vê sem rolar além do fim da página.
    let onde =
      posicao ?? (iSelecionado >= 0 ? iSelecionado + 1 : iRodape >= 0 ? iRodape : blocos.length)
    if (posicao === undefined && iRodape >= 0 && onde > iRodape) onde = iRodape

    blocos.splice(onde, 0, novo)
    alterar({ blocos })
    setSelecionado(novo.id)
  }

  function duplicarBloco(blocoId: string): void {
    if (!pagina) return
    const i = pagina.blocos.findIndex((b) => b.id === blocoId)
    const original = pagina.blocos[i]
    if (!original) return
    // Cópia profunda: `props` guarda listas (itens, campos, ações) e um spread
    // raso faria as duas cópias editarem o mesmo array.
    const copia: Bloco = { ...structuredClone(original), id: novoIdBloco() }
    const blocos = [...pagina.blocos]
    blocos.splice(i + 1, 0, copia)
    alterar({ blocos })
    setSelecionado(copia.id)
  }

  function removerBloco(blocoId: string): void {
    if (!pagina) return
    alterar({ blocos: pagina.blocos.filter((b) => b.id !== blocoId) })
    if (selecionado === blocoId) setSelecionado(null)
  }

  function alternarOculto(blocoId: string): void {
    if (!pagina) return
    alterar({
      blocos: pagina.blocos.map((x) => (x.id === blocoId ? { ...x, oculto: !x.oculto } : x)),
    })
  }

  function moverBloco(de: number, para: number): void {
    if (!pagina || de === para) return
    const blocos = [...pagina.blocos]
    const [item] = blocos.splice(de, 1)
    blocos.splice(para, 0, item!)
    alterar({ blocos })
  }

  /** Reordena pelo teclado — arrastar não é alcançável por quem não usa mouse. */
  function moverSelecionado(passo: -1 | 1): void {
    if (!pagina || !selecionado) return
    const de = pagina.blocos.findIndex((b) => b.id === selecionado)
    const para = de + passo
    if (de < 0 || para < 0 || para >= pagina.blocos.length) return
    moverBloco(de, para)
  }

  const salvar = useMutation({
    mutationFn: () =>
      api.put<Item<LandingPagina>>(`/api/v1/landing/${pagina!.id}`, {
        titulo: pagina!.titulo,
        categoriaId: pagina!.categoriaId ?? undefined,
        classeComunicacao: pagina!.classeComunicacao,
        blocos: pagina!.blocos,
        tema: pagina!.tema,
        seo: pagina!.seo,
        pixels: pagina!.pixels,
      }),
    onSuccess: (r) => {
      // A versão que acabou de ser gravada já está na tela: registrá-la como
      // adotada impede que um refetch a reimplante por cima da edição seguinte.
      versaoAdotada.current = r.dados.atualizadoEm
      setSujo(false)
      setErro(null)
      setAviso('Rascunho salvo.')
      void qc.invalidateQueries({ queryKey: ['landings'] })
    },
    onError: (e: Error) => setErro(e.message),
  })

  const publicar = useMutation({
    mutationFn: () => api.post<Item<unknown>>(`/api/v1/landing/${pagina!.id}/publicar`, {}),
    onSuccess: () => {
      setErro(null)
      setAviso('Página publicada.')
      void qc.invalidateQueries({ queryKey: ['landing', idOuSlug] })
      void qc.invalidateQueries({ queryKey: ['landings'] })
    },
    onError: (e: Error) => setErro(e.message),
  })

  // --------------------------------------------------------------- teclado

  useEffect(() => {
    function aoTeclar(e: KeyboardEvent): void {
      const alvo = e.target as HTMLElement | null
      const digitando =
        !!alvo &&
        (alvo.tagName === 'INPUT' || alvo.tagName === 'TEXTAREA' || alvo.isContentEditable)
      const comando = e.ctrlKey || e.metaKey

      if (comando && e.key.toLowerCase() === 's') {
        e.preventDefault()
        if (sujo && !salvar.isPending) salvar.mutate()
        return
      }
      // Dentro de um campo, Ctrl+Z é do campo: desfazer a página inteira
      // enquanto se corrige uma palavra seria uma surpresa cara.
      if (comando && !e.shiftKey && e.key.toLowerCase() === 'z' && !digitando) {
        e.preventDefault()
        desfazer()
        return
      }
      if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown') && !digitando) {
        e.preventDefault()
        moverSelecionado(e.key === 'ArrowUp' ? -1 : 1)
        return
      }
      if (e.key === 'Escape' && !digitando) setSelecionado(null)
    }
    window.addEventListener('keydown', aoTeclar)
    return () => window.removeEventListener('keydown', aoTeclar)
  })

  // Fechar a aba com rascunho não salvo é a perda mais banal e mais irritante.
  useEffect(() => {
    if (!sujo) return
    function avisar(e: BeforeUnloadEvent): void {
      e.preventDefault()
    }
    window.addEventListener('beforeunload', avisar)
    return () => window.removeEventListener('beforeunload', avisar)
  }, [sujo])

  return {
    pagina,
    paraRender,
    carregando: isLoading,
    falha: error ? ((error as Error).message ?? 'Falha ao carregar.') : null,
    selecionado,
    setSelecionado,
    sujo,
    erro,
    setErro,
    aviso,
    problemas,
    problemasPorBloco,
    problemasGerais: problemas.filter((p) => !p.blocoId),
    temHistorico: historico.current.length > 0,
    alterar,
    desfazer,
    inserirBloco,
    duplicarBloco,
    removerBloco,
    alternarOculto,
    moverBloco,
    moverSelecionado,
    salvar,
    publicar,
  }
}
